import { useMutation } from '@apollo/client';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import { BEACON_SEARCH_TARGETS } from '../constants/onboarding';
import { ExternalSearchMode } from '../graphql/generated/graphql';
import settingsIcon from '../icons/non-prefilled/16x16/settings.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import styles from './SearchManagePlatformButton.module.scss';
import SearchPlatformMenuItem, { Section } from './SearchPlatformMenuItem';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const REMOVE_EXTERNAL_SEARCH = gql(`
  mutation removeExternalSearch($platform: String!) {
    removeExternalSearch(platform: $platform)
  }
`);

const PLATFORMS_FRAGMENT = gql(`
  fragment SearchManagePlatformButton_platforms on ExternalSearchPlatform {
    personalAuthorized
    teamAuthorized
    installModes
    ...SearchPlatformMenuItem_platform
  }
`);

interface SearchManagePlatformButtonProps {
  refetch: () => unknown;
  platforms: DocumentType<typeof PLATFORMS_FRAGMENT>[];
}

const SearchManagePlatformButton = ({
  refetch,
  platforms,
}: SearchManagePlatformButtonProps) => {
  const userType = useAppSelector((state) => state.session.user.type);
  const sections = [
    {
      type: Section.Available,
      platforms: platforms.filter((p) => {
        const installModes = p.installModes || [];

        return (
          (installModes.includes(ExternalSearchMode.Personal) &&
            !p.personalAuthorized) ||
          (userType === 'ADMIN' &&
            installModes.includes(ExternalSearchMode.TeamWide) &&
            !p.teamAuthorized &&
            !p.personalAuthorized)
        );
      }),
    },
    {
      type: Section.Individual,
      platforms: platforms.filter((p) => p.personalAuthorized),
    },
    {
      type: Section.Team,
      platforms: platforms.filter((p) => p.teamAuthorized),
    },
  ].filter((s) => s.platforms.length > 0);
  const [removeExternalSearch] = useMutation(REMOVE_EXTERNAL_SEARCH);
  const handleRemove = async (platform: string) => {
    await removeExternalSearch({
      variables: { platform },
    });
    refetch();
  };

  return (
    <OverflowMenu
      button={
        <div className={styles.buttonWrapper}>
          <TutorialBeacon id={BEACON_SEARCH_TARGETS}>
            {(beaconProps) => (
              <div {...beaconProps}>
                <Button icon={settingsIcon} variant="secondary" size="small">
                  Manage
                </Button>
              </div>
            )}
          </TutorialBeacon>
        </div>
      }
      placement="bottom-start"
    >
      {sections.map((section, i) => (
        <div key={section.type}>
          {i > 0 && <MenuSeparator />}
          <span className={styles.settingsSectionTitle}>{section.type}</span>

          {section.platforms.map((platform) => (
            <SearchPlatformMenuItem
              key={platform.platform}
              type={section.type}
              platform={platform}
              onRemove={() => handleRemove(platform.platform)}
            />
          ))}
        </div>
      ))}
    </OverflowMenu>
  );
};

export default SearchManagePlatformButton;
